import { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';

// Lazy load the three separate apps for code splitting
const LandingApp = lazy(() => import('./LandingApp'));
const MainApp = lazy(() => import('./MainApp'));
const AdminApp = lazy(() => import('./AdminApp'));

// Lazy load legal pages
const Terms = lazy(() => import('./pages/static/Terms'));
const Privacy = lazy(() => import('./pages/static/Privacy'));
const CommunityGuidelines = lazy(() => import('./pages/static/CommunityGuidelines'));

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full"
      />
    </div>
  );
}

function App() {
  const [location] = useLocation();
  const hostname = window.location.hostname;
  
  // Determine if we're on the app subdomain
  const isAppDomain = ['app.gayborhood.io', 'app.outclose.com'].includes(hostname);
  
  // Set document title based on location
  useEffect(() => {
    if (location.startsWith('/app') || isAppDomain) {
      // MainApp will handle its own titles
    } else if (location.startsWith('/admin')) {
      // AdminApp will handle its own titles
    } else if (location === '/terms') {
      document.title = 'Terms of Service - OutClose';
    } else if (location === '/privacy') {
      document.title = 'Privacy Policy - OutClose';
    } else if (location === '/community-guidelines') {
      document.title = 'Community Guidelines - OutClose';
    } else if (location === '/') {
      // LandingApp will handle its own title
    } else {
      document.title = '404 - OutClose';
    }
  }, [location, isAppDomain]);

  // Determine which app to render based on the location and domain
  const renderApp = () => {
    if (location.startsWith('/admin')) {
      return <AdminApp />;
    }
    
    // Legal pages
    if (location === '/terms') {
      return <Terms />;
    }
    
    if (location === '/privacy') {
      return <Privacy />;
    }
    
    if (location === '/community-guidelines') {
      return <CommunityGuidelines />;
    }

    // If we're on app.*, always render MainApp
    if (isAppDomain || location.startsWith('/app')) {
      // Pass '/' as base path when on app subdomain, '/app' otherwise
      const basePath = isAppDomain ? '/' : '/app';
      return <MainApp basePath={basePath} />;
    }
        
    if (location === '/') {
      return <LandingApp />;
    }
    
    // 404
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
          <p className="text-dark-muted">Page not found</p>
        </div>
      </div>
    );
  };
  
  return (
    <Suspense fallback={<LoadingScreen />}>
      {renderApp()}
    </Suspense>
  );
}

export default App;
